import type { ApiRenterNotificationList } from 'src/types/notification';

import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { useRequestSession } from './useRequestSession';

const PAGE_SIZE = 20;

/**
 * The renter's notification feed (rental-clock v2.1 §5) — the cabinet channel for rental
 * events, scoped by account on the server. Read under the account session, whose token the api
 * client sends with every request, and keyed by that session like the deals the feed is about
 * (app#248).
 */
export function useRenterNotifications(params?: { enabled?: boolean }) {
  const session = useRequestSession();

  return useInfiniteQuery({
    queryKey: QueryKeys.forSession(QueryKeys.renterNotifications(), session),
    enabled: params?.enabled ?? true,
    staleTime: LIVE_STALE_TIME,
    queryFn: async ({ pageParam = 0 }): Promise<ApiRenterNotificationList & { offset: number }> => {
      const response = await axios.get<ApiRenterNotificationList>('/buyer/notifications', {
        params: { limit: PAGE_SIZE, offset: pageParam },
      });
      return { ...response.data, offset: pageParam as number };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + PAGE_SIZE;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
  });
}

/** Marks one notification read (idempotent server-side); refreshes the badge. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await axios.post(`/buyer/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.renterNotifications() });
    },
  });
}

/** Marks the whole feed read — the "clear the badge" action of the screen. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ markedCount: number }> => {
      const response = await axios.post<{ markedCount: number }>('/buyer/notifications/read-all');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.renterNotifications() });
    },
  });
}
